import {
  ButtonProps,
  DataStatus,
  closeCurrentDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {defaultTo, equals, isNil, not} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {
  PostServiceOrderItemRequest,
  PostWarehouseArticleItemRequest,
  ServiceOrderIssueNoteBasketItem,
  useBulkDeleteServiceOrderIssueNoteItemsMutation,
  useDeleteServiceOrderIssueNoteBasketMutation,
  useGetServiceOrderIssueNoteBasketQuery,
  useGetServiceOrderIssueNoteQuery,
  usePatchServiceOrderIssueNoteBasketCheckoutMutation,
  usePatchServiceOrderIssueNoteBasketItemQuantityMutation,
  usePostServiceOrderItemMutation,
  usePostWarehouseArticleItemMutation,
} from '@dms/api/warehouse';
import {
  useGetServiceOrderVariantQuery,
  usePatchOrderItemForMaterialMutation,
  usePutServiceOrderItemsToOtherOrderJobMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  AfterSalesMaterialBasket,
  EitherQuantityOrError,
  FullScreenModal,
  MaterialBasketItemEditingDetails,
  handleApiError,
  logError,
  refreshDmsDatagrid,
  useBasketItemsQuantityState,
} from '@dms/shared';
import {
  EditMaterialItem,
  useAfterSalesHeaderParameters,
  useBasketMechanic,
} from '@dms/shared-aftersales';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {RequiredTestIdProps, composePath, suffixTestId, useNavigate} from 'shared/web';

import {EmptyServiceOrderIssueDialog} from './EmptyServiceOrderIssueDialog';
import {MaterialList} from './MaterialList';

interface ServiceOrderIssueAddMaterialModalProps extends RequiredTestIdProps {
  onDiscard: () => void;
  onSubmit: () => void;
}

export function ServiceOrderIssueAddMaterialModal(props: ServiceOrderIssueAddMaterialModalProps) {
  const {serviceOrderIssueNoteId} = useWarehouseParams();

  const navigate = useNavigate();

  const {
    data: serviceOrderIssueNote,
    isLoading: isServiceOrderIssueNoteLoading,
    isError: hasServiceOrderIssueNoteError,
  } = useGetServiceOrderIssueNoteQuery({
    serviceOrderIssueNoteId,
  });

  const headerParameters = useAfterSalesHeaderParameters({
    numberLabel: i18n.t('entity.warehouse.labels.serviceOrder'),
    number: serviceOrderIssueNote?.serviceOrder?.number,
    customerLabel: i18n.t('entity.warehouse.labels.customer'),
    customerId: serviceOrderIssueNote?.serviceOrder?.customerId,
    vehicleId: serviceOrderIssueNote?.serviceOrder?.vehicleId,
  });

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetServiceOrderIssueNoteBasketQuery({
    serviceOrderIssueNoteId,
  });

  const {
    data: serviceOrderVariant,
    isLoading: isServiceOrderVariantLoading,
    isError: hasServiceOrderVariantError,
  } = useGetServiceOrderVariantQuery(
    {serviceOrderVariantId: serviceOrderIssueNote?.serviceOrder?.serviceOrderVariantId as string},
    {skip: isNilOrEmpty(serviceOrderIssueNote?.serviceOrder?.serviceOrderVariantId)}
  );

  const [addServiceOrderItemToBasket, serviceOrderItemResult] = usePostServiceOrderItemMutation();

  const [addWarehouseArticleToBasket, warehouseArticleResult] =
    usePostWarehouseArticleItemMutation();

  const [discardBasket, {isLoading: isDiscardingBasket}] =
    useDeleteServiceOrderIssueNoteBasketMutation({fixedCacheKey: 'discard-soi-basket'});

  const [patchServiceOrderIssueNoteBasketCheckout, {isLoading: isCheckingOutBasket}] =
    usePatchServiceOrderIssueNoteBasketCheckoutMutation();

  const [patchServiceOrderIssueNoteBasketItemQuantity, {isLoading: isChangingQuantity}] =
    usePatchServiceOrderIssueNoteBasketItemQuantityMutation();

  const [deleteBasketItems, {isLoading: isDeletingBasketItems}] =
    useBulkDeleteServiceOrderIssueNoteItemsMutation();

  const [, {isLoading: isMovingItem}] = usePutServiceOrderItemsToOtherOrderJobMutation({
    fixedCacheKey: 'basket-item-job-change',
  });

  const [, {isLoading: isEditingMaterialItem}] = usePatchOrderItemForMaterialMutation({
    fixedCacheKey: 'edit-service-order-item',
  });

  const serviceCaseId = defaultTo('', serviceOrderIssueNote?.serviceCaseId);

  const serviceOrderId = defaultTo('', serviceOrderIssueNote?.serviceOrderId);

  const basketItems = basket?.sparePartsBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const basketTotalPrice = basket?.sparePartsBasketTotalPrice;

  const basketMechanicId = basket?.assignMechanicId;

  const isLoading = isServiceOrderIssueNoteLoading || isServiceOrderVariantLoading;

  const hasError = hasServiceOrderIssueNoteError || hasServiceOrderVariantError;

  const isAddingItemToBasket = serviceOrderItemResult.isLoading || warehouseArticleResult.isLoading;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItemToBasket ||
    isDiscardingBasket ||
    isCheckingOutBasket ||
    isChangingQuantity ||
    isEditingMaterialItem ||
    isDeletingBasketItems ||
    isMovingItem;

  const {setBasketItemQuantity, hasInvalidBasketItems, hasUnsettledQuantity} =
    useBasketItemsQuantityState(basketItems);

  const {
    mechanics,
    areMechanicsLoading,
    mechanicPayload,
    inputMechanicId,
    mechanicError,
    handleMechanicChange,
    setMechanicError,
  } = useBasketMechanic({
    authorizationProfileId: serviceOrderVariant?.authorizationProfileId,
    assignedMechanicId: basketMechanicId,
  });

  const handleCheckout = async () => {
    if (isNil(mechanicPayload)) {
      return setMechanicError(i18n.t('general.notifications.fieldIsRequired'));
    }

    await patchServiceOrderIssueNoteBasketCheckout({
      serviceOrderIssueNoteId,
      body: {
        mechanic: mechanicPayload,
      },
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.itemsAdded')))
      .then(() =>
        navigate(
          composePath(warehouseRoutes.serviceOrderIssueDetailOverview, {
            params: {id: serviceOrderIssueNoteId},
          })
        )
      )
      .then(props.onSubmit)
      .catch(handleApiError);
  };

  const handleDiscard = async () => {
    await discardBasket({
      serviceOrderIssueNoteId,
    })
      .unwrap()
      .then((result) => {
        if (result.deleteEmptySoiPrompt) {
          return openDialog(
            <EmptyServiceOrderIssueDialog
              onDelete={() => handleResolveDecision('DELETE')}
              onDontDelete={() => handleResolveDecision('DONT_DELETE')}
            />,
            {id: EMPTY_SOI_DIALOG, size: 'small'}
          );
        }
        props.onDiscard();
      })
      .catch(handleApiError);
  };

  const handleResolveDecision = (decision: 'DELETE' | 'DONT_DELETE') =>
    match(decision)
      .with('DELETE', () => {
        discardBasket({serviceOrderIssueNoteId, body: {deleteEmptySoiPromptResponseCode: decision}})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .then(() => {
            closeCurrentDialog();
            props.onDiscard();
            navigate(composePath(warehouseRoutes.serviceOrderIssues));
          })
          .catch(handleApiError);
      })
      .with('DONT_DELETE', () => {
        discardBasket({serviceOrderIssueNoteId, body: {deleteEmptySoiPromptResponseCode: decision}})
          .unwrap()
          .then(() => {
            closeCurrentDialog();
            props.onDiscard();
          })
          .catch(handleApiError);
      })
      .exhaustive();

  const handleAddServiceOrderItemToBasket = (arg: PostServiceOrderItemRequest) =>
    addServiceOrderItemToBasket(arg).unwrap().catch(handleApiError);

  const handleAddWarehouseArticleToBasket = (arg: PostWarehouseArticleItemRequest) =>
    addWarehouseArticleToBasket(arg).unwrap().catch(handleApiError);

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setBasketItemQuantity(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await patchServiceOrderIssueNoteBasketItemQuantity({
      serviceOrderIssueNoteId,
      itemId,
      body: {quantity: quantity.newQuantity},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleEdit = (editingDetails: MaterialBasketItemEditingDetails) => {
    if (isNil(editingDetails?.itemId)) {
      return logError('itemId is not defined');
    }

    if (isNil(editingDetails?.serviceCaseId)) {
      return logError('serviceCaseId is not defined');
    }

    if (isNil(editingDetails?.serviceOrderId)) {
      return logError('serviceOrderId is not defined');
    }

    if (isNil(editingDetails?.serviceJobId)) {
      return logError('serviceJobId is not defined');
    }

    openDialog(
      <EditMaterialItem
        itemId={editingDetails.itemId}
        serviceCaseId={editingDetails.serviceCaseId}
        serviceOrderId={editingDetails.serviceOrderId}
        serviceJobId={editingDetails.serviceJobId}
        serviceOrderIssueNoteId={serviceOrderIssueNoteId}
        onClose={closeCurrentDialog}
      />,
      {title: editingDetails.itemName}
    );
  };

  const handleDelete = async (itemsIds: string | string[]) => {
    const ids = isArray(itemsIds) ? itemsIds : [itemsIds];

    const requestBody = {
      serviceOrderIssueNoteItemIds: ids,
    };

    await deleteBasketItems({
      serviceOrderIssueNoteId,
      body: requestBody,
    })
      .unwrap()
      .then(() => refreshDmsDatagrid('issue-note-service-order-items'))
      .catch(handleApiError);
  };

  const actions: ButtonProps[] = [
    {
      isLoading: isDiscardingBasket,
      isDisabled: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleDiscard,
    },
    {
      isLoading: isCheckingOutBasket,
      isDisabled: isBasketEmpty || hasPendingRequest || hasUnsettledQuantity,
      title: i18n.t('general.actions.add'),
      variant: 'primary',
      onClick: handleCheckout,
      'data-testid': suffixTestId('headerActions.add', props),
    },
  ];

  return (
    <FullScreenModal
      headline={i18n.t('general.actions.addMaterial')}
      parameters={headerParameters}
      actions={actions}
    >
      <DataStatus isLoading={isLoading} isError={hasError} minHeight="100%">
        <Box padding={4} height="100%">
          <HStack spacing={4} height="100%">
            <Box flex={5}>
              <MaterialList
                serviceOrderIssueNote={serviceOrderIssueNote}
                onAddServiceOrderItemToBasket={handleAddServiceOrderItemToBasket}
                onAddWarehouseArticleToBasket={handleAddWarehouseArticleToBasket}
                hasPendingRequest={hasPendingRequest}
                data-testid={suffixTestId('list', props)}
              />
            </Box>
            <Box flex={1} minWidth={86}>
              <AfterSalesMaterialBasket<ServiceOrderIssueNoteBasketItem>
                basket={{
                  items: basketItems,
                  totalPrice: basketTotalPrice,
                  isLoading: isBasketLoading,
                  hasError: hasBasketError,
                  hasPendingRequest,
                  hasInvalidItems: hasInvalidBasketItems,
                }}
                mechanic={{
                  mechanics,
                  areMechanicsLoading,
                  isMechanicRequired: true,
                  selectedMechanicId: inputMechanicId,
                  mechanicError,
                  onMechanicChange: handleMechanicChange,
                }}
                jobChange={{
                  canPerformJobChange: true,
                  serviceCaseId,
                  serviceOrderId,
                }}
                onQuantityChange={handleQuantityChange}
                onEdit={handleEdit}
                onDelete={handleDelete}
                data-testid={suffixTestId('basket', props)}
              />
            </Box>
          </HStack>
        </Box>
      </DataStatus>
    </FullScreenModal>
  );
}

const EMPTY_SOI_DIALOG = 'empty-service-order-issue-dialog';
